import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import investorService from '../services/investorService';
import InvestorLayout from '../components/InvestorLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { 
  Building2, 
  Heart, 
  FileText, 
  TrendingUp,
  Clock,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  RefreshCw
} from 'lucide-react';

const InvestorDashboardPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [stats, setStats] = useState(null);
  const [recentViews, setRecentViews] = useState([]);
  const [activeInvestments, setActiveInvestments] = useState([]);
  const [pendingDocuments, setPendingDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDashboardData = useCallback(async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      setError(null);

      // Paralel olarak tüm verileri çek
      const [statsResult, viewsResult, investmentsResult, docsResult] = await Promise.all([
        investorService.getDashboardStats(user.id),
        investorService.getViewHistory(user.id, 5),
        investorService.getInvestments(user.id),
        investorService.getDocuments(user.id),
      ]);

      if (statsResult.error) throw statsResult.error;
      if (viewsResult.error) throw viewsResult.error;
      if (investmentsResult.error) throw investmentsResult.error;
      if (docsResult.error) throw docsResult.error;

      setStats(statsResult.data);
      setRecentViews(viewsResult.data || []);
      
      // Aktif yatırımları filtrele (cancelled ve completed hariç)
      const activeInvs = (investmentsResult.data || []).filter(
        inv => !['cancelled', 'completed'].includes(inv.status)
      );
      setActiveInvestments(activeInvs);
      
      // Bekleyen belgeleri filtrele
      const pendingDocs = (docsResult.data || []).filter(
        doc => doc.status === 'pending'
      );
      setPendingDocuments(pendingDocs);

    } catch (err) {
      console.error('Dashboard data fetch error:', err);
      setError(err.message || t('investor.dashboard.error_loading'));
    } finally {
      setLoading(false);
    }
  }, [user?.id, t]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Status badge renklerini belirle
  const getStatusColor = (status) => {
    const colors = {
      interested: 'bg-gray-100 text-gray-700 hover:bg-gray-200',
      offer_made: 'bg-blue-100 text-blue-700 hover:bg-blue-200',
      negotiating: 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200',
      contract_signed: 'bg-green-100 text-green-700 hover:bg-green-200',
      deposit_paid: 'bg-purple-100 text-purple-700 hover:bg-purple-200',
      completed: 'bg-green-600 text-white hover:bg-green-700',
      cancelled: 'bg-red-100 text-red-700 hover:bg-red-200',
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  // Status badge ikonunu belirle
  const getStatusIcon = (status) => {
    const icons = {
      interested: Clock,
      offer_made: FileText,
      negotiating: TrendingUp,
      contract_signed: CheckCircle,
      deposit_paid: CheckCircle,
      completed: CheckCircle,
      cancelled: AlertCircle,
    };
    return icons[status] || Clock;
  };

  // İstatistik kartları için skeleton
  const StatCardSkeleton = () => (
    <Card className="border border-gray-200">
      <CardContent className="p-6">
        <Skeleton className="h-12 w-12 rounded-lg mb-4" />
        <Skeleton className="h-8 w-24 mb-2" />
        <Skeleton className="h-6 w-16" />
      </CardContent>
    </Card>
  );

  // İstatistik kartı bileşeni
  const StatCard = ({ title, value, icon: Icon, color, onClick }) => (
    <Card 
      className={`border border-gray-200 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer ${
        onClick ? 'hover:border-[#D4AF37]' : ''
      }`}
      onClick={onClick}
    >
      <CardContent className="p-6">
        <div className={`inline-flex p-3 rounded-lg ${color} mb-4`}>
          <Icon size={24} className="text-white" />
        </div>
        <h3 className="text-sm text-gray-600 font-medium mb-1">{title}</h3>
        <p className="text-3xl font-bold text-[#0A192F]">{value}</p>
      </CardContent>
    </Card>
  );

  if (error) {
    return (
      <InvestorLayout>
        <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
          <AlertCircle size={48} className="text-red-500 mb-4" />
          <h2 className="text-2xl font-heading text-[#0A192F] mb-2">
            {t('investor.dashboard.error_title')}
          </h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button 
            onClick={fetchDashboardData}
            className="bg-[#0A192F] text-white hover:bg-[#112240]"
          >
            <RefreshCw size={20} className="mr-2" />
            {t('investor.dashboard.retry')}
          </Button>
        </div>
      </InvestorLayout>
    );
  }

  return (
    <InvestorLayout>
      <div className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-heading text-[#0A192F] mb-2">
            {t('investor.dashboard.title')}
          </h1>
          <p className="text-gray-600">
            {t('investor.dashboard.welcome')}
          </p>
        </div>

        {/* İstatistik Kartları */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {loading ? (
            <>
              <StatCardSkeleton />
              <StatCardSkeleton />
              <StatCardSkeleton />
              <StatCardSkeleton />
            </>
          ) : (
            <>
              <StatCard
                title={t('investor.dashboard.activeInvestments')}
                value={stats?.activeInvestments || 0}
                icon={Building2}
                color="bg-blue-500"
                onClick={() => navigate('/tr/investor/investments')}
              />
              <StatCard
                title={t('investor.dashboard.myFavorites')}
                value={stats?.totalFavorites || 0}
                icon={Heart}
                color="bg-red-500"
                onClick={() => navigate('/tr/investor/favorites')}
              />
              <StatCard
                title={t('investor.dashboard.pendingDocuments')}
                value={stats?.pendingDocuments || 0}
                icon={FileText}
                color="bg-orange-500"
                onClick={() => navigate('/tr/investor/documents')}
              />
              <StatCard
                title={t('investor.dashboard.totalInvestment')}
                value={`£${(stats?.totalInvested || 0).toLocaleString()}`}
                icon={TrendingUp}
                color="bg-green-500"
              />
            </>
          )}
        </div>

        {/* İçerik Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Sol Kolon - Son Görüntülenenler ve Yatırımlar */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Son Görüntülenen İlanlar */}
            <Card className="border border-gray-200">
              <CardHeader className="border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-heading text-[#0A192F]">
                    {t('investor.dashboard.recentViews')}
                  </CardTitle>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => navigate('/tr/investor/investments')}
                  >
                    {t('common.view_all')}
                    <ArrowRight size={16} className="ml-1" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                {loading ? (
                  <div className="space-y-4">
                    {[1, 2, 3].map((i) => (
                      <Skeleton key={i} className="h-20 w-full" />
                    ))}
                  </div>
                ) : recentViews.length > 0 ? (
                  <div className="space-y-4">
                    {recentViews.map((view) => (
                      <div
                        key={view.id}
                        className="flex items-center space-x-4 p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                        onClick={() => navigate(`/tr/properties/${view.property_id}`)}
                      >
                        <div className="w-16 h-16 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                          {view.property?.images?.[0] ? (
                            <img
                              src={view.property.images[0]}
                              alt={view.property.title}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full bg-gray-300 flex items-center justify-center">
                              <Building2 size={24} className="text-gray-400" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-[#0A192F] truncate">
                            {view.property?.title || 'Property'}
                          </h4>
                          <p className="text-sm text-gray-500">
                            {view.property?.location || 'Location'}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-[#0A192F]">
                            {view.property?.price 
                              ? `£${Number(view.property.price).toLocaleString()}`
                              : 'Price not set'}
                          </p>
                          <Button size="sm" variant="outline" className="mt-1">
                            {t('common.view')}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Building2 size={48} className="mx-auto mb-3 text-gray-300" />
                    <p>{t('investor.dashboard.no_recent_views')}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Aktif Yatırımlarım */}
            <Card className="border border-gray-200">
              <CardHeader className="border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-heading text-[#0A192F]">
                    {t('investor.dashboard.myInvestments')}
                  </CardTitle>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => navigate('/tr/investor/investments')}
                  >
                    {t('common.view_all')}
                    <ArrowRight size={16} className="ml-1" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                {loading ? (
                  <div className="space-y-4">
                    {[1, 2, 3].map((i) => (
                      <Skeleton key={i} className="h-24 w-full" />
                    ))}
                  </div>
                ) : activeInvestments.length > 0 ? (
                  <div className="space-y-4">
                    {activeInvestments.map((investment) => {
                      const StatusIcon = getStatusIcon(investment.status);
                      return (
                        <div
                          key={investment.id}
                          className="p-4 rounded-lg border border-gray-200 hover:border-[#D4AF37] transition-colors"
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <h4 className="font-semibold text-[#0A192F] mb-1">
                                {investment.property?.title || 'Property'}
                              </h4>
                              <p className="text-sm text-gray-500">
                                {investment.property?.location || 'Location'}
                              </p>
                            </div>
                            <Badge className={getStatusColor(investment.status)}>
                              <StatusIcon size={14} className="mr-1" />
                              {t(`investor.status.${investment.status}`)}
                            </Badge>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-500">
                              {t('investor.dashboard.last_update')}: {' '}
                              {new Date(investment.updated_at).toLocaleDateString()}
                            </span>
                            {investment.offer_amount && (
                              <span className="font-semibold text-[#0A192F]">
                                {t('investor.dashboard.offer')}: £{Number(investment.offer_amount).toLocaleString()}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Building2 size={48} className="mx-auto mb-3 text-gray-300" />
                    <p>{t('investor.dashboard.no_investments')}</p>
                    <Button 
                      className="mt-4 bg-[#D4AF37] text-white hover:bg-[#B5952F]"
                      onClick={() => navigate('/tr')}
                    >
                      {t('investor.dashboard.browse_properties')}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sağ Kolon - Bekleyen Belgeler ve KKTC Bilgi */}
          <div className="space-y-6">
            
            {/* Bekleyen Belgeler */}
            <Card className="border border-gray-200">
              <CardHeader className="border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-heading text-[#0A192F]">
                    {t('investor.dashboard.documentsNeeded')}
                  </CardTitle>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => navigate('/tr/investor/documents')}
                  >
                    {t('common.view_all')}
                    <ArrowRight size={16} className="ml-1" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                {loading ? (
                  <div className="space-y-4">
                    {[1, 2, 3].map((i) => (
                      <Skeleton key={i} className="h-16 w-full" />
                    ))}
                  </div>
                ) : pendingDocuments.length > 0 ? (
                  <div className="space-y-3">
                    {pendingDocuments.map((doc) => (
                      <div
                        key={doc.id}
                        className="p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium text-[#0A192F] text-sm">
                            {t(`investor.document_type.${doc.document_type}`)}
                          </h4>
                          <Badge variant="outline" className="text-xs">
                            {t('investor.document_status.pending')}
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span>
                            {new Date(doc.uploaded_at).toLocaleDateString()}
                          </span>
                          <Button size="sm" variant="outline">
                            {doc.file_url ? t('common.view') : t('common.upload')}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-gray-500">
                    <CheckCircle size={36} className="mx-auto mb-2 text-green-500" />
                    <p className="text-sm">{t('investor.dashboard.all_documents_complete')}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* KKTC Uyumluluk Bölümü */}
            <Card className="border border-[#D4AF37] bg-gradient-to-br from-[#0A192F] to-[#112240] text-white">
              <CardContent className="p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-2 bg-[#D4AF37]/20 rounded-lg">
                    <FileText size={24} className="text-[#D4AF37]" />
                  </div>
                  <h3 className="font-heading font-semibold">
                    {t('investor.dashboard.kktcInfo')}
                  </h3>
                </div>
                <p className="text-sm text-white/80 mb-4">
                  {t('investor.dashboard.kktcInfoDesc')}
                </p>
                <Button 
                  className="w-full bg-[#D4AF37] text-white hover:bg-[#B5952F]"
                  onClick={() => navigate('/tr/investor/documents')}
                >
                  {t('investor.dashboard.kktcInfoButton')}
                  <ArrowRight size={16} className="ml-2" />
                </Button>
              </CardContent>
            </Card>

          </div>
        </div>
      </div>
    </InvestorLayout>
  );
};

export default InvestorDashboardPage;
